// ============================================================
// CalendarMiniMonth.tsx
// A compact month-calendar for the Diary's "jump to date" popover — pick
// ANY day and the calendar jumps there (the parent decides how, view-
// aware: Day anchors the day, Week its week, Month its month). Built from
// the app's "B in Casevo" Casevo tokens; Mon-first, today ringed, the
// currently-focused day filled. Owns its own month navigation (‹ ›) so
// the user can page to a far month before picking.
// ============================================================
import React, { useMemo, useState } from 'react'

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

const ymd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

interface CalendarMiniMonthProps {
  // The day the calendar is currently focused on (drives the initial month
  // shown + the filled "selected" cell).
  value: Date
  onPick: (date: Date) => void
}

const CalendarMiniMonth: React.FC<CalendarMiniMonthProps> = ({ value, onPick }) => {
  const [viewMonth, setViewMonth] = useState<Date>(
    () => new Date(value.getFullYear(), value.getMonth(), 1),
  )
  const todayKey = ymd(new Date())
  const selectedKey = ymd(value)

  const cells = useMemo<Date[]>(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const first = new Date(year, month, 1)
    const leading = (first.getDay() + 6) % 7 // Mon=0
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const total = Math.ceil((leading + daysInMonth) / 7) * 7
    return Array.from({ length: total }, (_, i) => new Date(year, month, 1 - leading + i))
  }, [viewMonth])

  const monthLabel = viewMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  return (
    <div className="w-64 select-none">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          aria-label="Previous month"
          className="w-7 h-7 flex items-center justify-center text-casevo-mid rounded-md hover:bg-casevo-mint/30 hover:text-casevo-forest transition-colors focus:outline-none casevo-focus"
        >
          ‹
        </button>
        <span className="text-sm font-medium text-casevo-forest">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          aria-label="Next month"
          className="w-7 h-7 flex items-center justify-center text-casevo-mid rounded-md hover:bg-casevo-mint/30 hover:text-casevo-forest transition-colors focus:outline-none casevo-focus"
        >
          ›
        </button>
      </div>
      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="text-center text-[10px] uppercase tracking-wide text-casevo-mid">
            {w}
          </div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d) => {
          const key = ymd(d)
          const inMonth = d.getMonth() === viewMonth.getMonth()
          const isToday = key === todayKey
          const isSelected = key === selectedKey
          const base =
            'h-8 rounded-md text-xs flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint'
          const tone = isSelected
            ? 'bg-casevo-primary text-white'
            : isToday
              ? 'border border-casevo-brand text-casevo-brand hover:bg-casevo-mint/30'
              : `text-casevo-forest hover:bg-casevo-mint/30 ${inMonth ? '' : 'opacity-40'}`
          return (
            <button key={key} type="button" onClick={() => onPick(d)} className={`${base} ${tone}`}>
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default CalendarMiniMonth
